/**
 * Calendly Integration API
 *
 * Functions to interact with Calendly edge functions
 */

import { createClient } from '@/lib/supabase/client'
import type {
  CalendlyOAuthInitResponse,
  CalendlySyncResponse,
  CalendlySchedule,
  CalendlySchedulingLink,
  Integration,
} from './types'

const FUNCTIONS_BASE = import.meta.env.VITE_SUPABASE_URL + '/functions/v1'

/**
 * Get the current session token for authenticated requests
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const supabase = createClient()
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  }
}

/**
 * Initiate Calendly OAuth flow
 * Returns the authorization URL to redirect the user to
 */
export async function initiateCalendlyOAuth(
  teamId: string,
  redirectPath: string = '/integrations'
): Promise<CalendlyOAuthInitResponse> {
  const headers = await getAuthHeaders()

  const response = await fetch(`${FUNCTIONS_BASE}/calendly-oauth-init`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ team_id: teamId, redirect_path: redirectPath }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to initiate OAuth' }))
    throw new Error(error.message || 'Failed to initiate Calendly OAuth')
  }

  return response.json()
}

/**
 * Sync event types from Calendly
 * Fetches the latest event types and updates local schedules
 */
export async function syncCalendlyEventTypes(teamId: string): Promise<CalendlySyncResponse> {
  const headers = await getAuthHeaders()

  const response = await fetch(`${FUNCTIONS_BASE}/calendly-sync-event-types`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ team_id: teamId }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to sync event types' }))
    throw new Error(error.message || 'Failed to sync Calendly event types')
  }

  return response.json()
}

/**
 * Create a single-use scheduling link for a guest
 */
export async function createBookingLink(
  appearanceId: string,
  scheduleId: string
): Promise<CalendlySchedulingLink> {
  const headers = await getAuthHeaders()

  const response = await fetch(`${FUNCTIONS_BASE}/calendly-create-booking-link`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ appearance_id: appearanceId, schedule_id: scheduleId }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create booking link' }))
    throw new Error(error.message || 'Failed to create booking link')
  }

  return response.json()
}

/**
 * Disconnect Calendly integration
 */
export async function disconnectCalendly(teamId: string): Promise<void> {
  const supabase = createClient()

  // Use type assertion for the update - Supabase generic inference sometimes fails
  const updateData: Partial<Integration> = {
    status: 'disconnected',
    access_token: null,
    refresh_token: null,
    token_expires_at: null,
    config: null,
    error_message: null,
  }

  const { error } = await (supabase
    .from('integrations') as ReturnType<typeof supabase.from>)
    .update(updateData)
    .eq('team_id', teamId)
    .eq('provider', 'calendly')

  if (error) {
    throw new Error('Failed to disconnect Calendly')
  }

  // Also delete synced schedules
  await (supabase
    .from('calendly_schedules') as ReturnType<typeof supabase.from>)
    .delete()
    .eq('team_id', teamId)
}

/**
 * Get Calendly integration status for a team
 */
export async function getCalendlyIntegration(teamId: string): Promise<Integration | null> {
  const supabase = createClient()
  const { data, error } = await (supabase
    .from('integrations') as ReturnType<typeof supabase.from>)
    .select('*')
    .eq('team_id', teamId)
    .eq('provider', 'calendly')
    .single()

  if (error && error.code !== 'PGRST116') {
    throw error
  }

  return data as Integration | null
}

/**
 * Get all synced Calendly schedules for a team
 */
export async function getCalendlySchedules(teamId: string): Promise<CalendlySchedule[]> {
  const supabase = createClient()
  const { data, error } = await (supabase
    .from('calendly_schedules') as ReturnType<typeof supabase.from>)
    .select('*')
    .eq('team_id', teamId)
    .eq('is_active', true)
    .order('is_default', { ascending: false })
    .order('name')

  if (error) {
    throw error
  }

  return (data as CalendlySchedule[]) || []
}

/**
 * Set a schedule as the default for a team
 */
export async function setDefaultSchedule(scheduleId: string): Promise<void> {
  const supabase = createClient()
  const { error } = await (supabase
    .from('calendly_schedules') as ReturnType<typeof supabase.from>)
    .update({ is_default: true })
    .eq('id', scheduleId)

  if (error) {
    throw new Error('Failed to set default schedule')
  }
}

/**
 * Get the default schedule for a team
 */
export async function getDefaultSchedule(teamId: string): Promise<CalendlySchedule | null> {
  const supabase = createClient()
  const { data, error } = await (supabase
    .from('calendly_schedules') as ReturnType<typeof supabase.from>)
    .select('*')
    .eq('team_id', teamId)
    .eq('is_default', true)
    .single()

  if (error && error.code !== 'PGRST116') {
    throw error
  }

  return data as CalendlySchedule | null
}

/**
 * Link a schedule to an episode
 */
export async function linkScheduleToEpisode(
  episodeId: string,
  scheduleId: string
): Promise<void> {
  const supabase = createClient()
  const { error } = await (supabase
    .from('episode_schedules') as ReturnType<typeof supabase.from>)
    .upsert(
      { episode_id: episodeId, schedule_id: scheduleId },
      { onConflict: 'episode_id' }
    )

  if (error) {
    throw new Error('Failed to link schedule to episode')
  }
}

/**
 * Get the schedule linked to an episode
 */
export async function getEpisodeSchedule(episodeId: string): Promise<CalendlySchedule | null> {
  const supabase = createClient()
  const { data, error } = await (supabase
    .from('episode_schedules') as ReturnType<typeof supabase.from>)
    .select('schedule:calendly_schedules(*)')
    .eq('episode_id', episodeId)
    .single()

  if (error && error.code !== 'PGRST116') {
    throw error
  }

  const result = data as { schedule: CalendlySchedule } | null
  return result?.schedule ?? null
}
